import { forwardRef, KeyboardEvent, ChangeEvent } from 'react';
import CleanNumberInput, { CleanNumberInputProps } from './CleanNumberInput';
import { isDigitString } from '../../DatePicker/dateValidation';
type ChainableCleanNumberFieldProps = CleanNumberInputProps & {
  onLeftBound?: () => void;
  onRightBound?: () => void;
};

const ChainableCleanNumberField = ({
  onLeftBound,
  onRightBound,
  value,
  maxLength,
  onKeyDown,
  onFocus,
  onChange,
  ...rest
}: ChainableCleanNumberFieldProps) => {
  const caretPositionAt = (input: HTMLInputElement, pos: number) =>
    input.selectionStart === pos && input.selectionEnd === pos;
  const isAtInputEnd = (input: HTMLInputElement) =>
    maxLength && caretPositionAt(input, maxLength);
  const isAtInputStart = (input: HTMLInputElement) => caretPositionAt(input, 0);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);

    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter') {
      return onRightBound?.();
    }

    if (isAtInputStart(input) && event.key === 'Backspace') {
      return onLeftBound?.();
    }

    // This makes jump to right, when input is focused at the end of it (via mouse or tap) and then user
    // presses key. That doesn't trigger onChange event (because of maxLength),
    // but cursor still need to move to the right if it's a digit
    if (isDigitString(event.key) && isAtInputEnd(input)) {
      return onRightBound?.();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Another check has to be done onChange. If it's onKeyDown caret position is still previous one,
    // Which means onRightBound() would be only executed on next keydown.
    // Simply: this makes jump to right, after last allowed symbol, while typing continuosly
    const input = event.target as HTMLInputElement;
    if (isAtInputEnd(input)) onRightBound?.();

    onChange?.(event);
  };

  return (
    <CleanNumberInput
      {...rest}
      value={value}
      maxLength={maxLength}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onFocus={onFocus}
      onClick={(event) => {
        (event.target as HTMLInputElement).select();
      }}
    />
  );
};

const ChainableNumberFieldWithRef = forwardRef(
  (props: ChainableCleanNumberFieldProps, ref) => (
    <ChainableCleanNumberField inputRef={ref} {...props} />
  )
);

export default ChainableNumberFieldWithRef;
